import React, { useState, useEffect } from "react";
import { auditLogAPI, userAPI } from "../../lib/api";
import { toast } from "sonner";
import { formatDateTime, capitalizeFirst } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { ClipboardList, Filter, Download, FileText } from "lucide-react";

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState("all");

  const entityTypes = ["tenant", "user", "department", "designation", "leave", "resignation", "notice", "document", "salary_structure", "geo_location"];

  useEffect(() => {
    fetchData();
  }, [entityFilter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (entityFilter !== "all") {
        params.entity_type = entityFilter;
      }
      const [logsRes, usersRes] = await Promise.all([
        auditLogAPI.getAll(params),
        userAPI.getAll(),
      ]);
      setLogs(logsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Failed to fetch audit logs");
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (userId) => {
    const user = users.find((u) => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : "System";
  };

  const getActionColor = (action) => {
    const colors = {
      CREATE: "bg-emerald-100 text-emerald-700",
      UPDATE: "bg-blue-100 text-blue-700",
      DELETE: "bg-red-100 text-red-700",
      SUSPEND: "bg-amber-100 text-amber-700",
      ACTIVATE: "bg-emerald-100 text-emerald-700",
      APPROVE: "bg-emerald-100 text-emerald-700",
      REJECT: "bg-red-100 text-red-700",
      SUBMIT: "bg-violet-100 text-violet-700",
      COMPLETE: "bg-emerald-100 text-emerald-700",
      DEACTIVATE: "bg-red-100 text-red-700",
      ASSIGN_HR_ADMIN: "bg-indigo-100 text-indigo-700",
      ASSIGN_MANAGER: "bg-indigo-100 text-indigo-700",
    };
    return colors[action] || "bg-slate-100 text-slate-700";
  };

  const handleExportCSV = () => {
    const headers = ["Timestamp", "User", "Action", "Entity Type", "Entity ID", "Changes"];
    const rows = logs.map(log => [
      log.created_at,
      getUserName(log.user_id),
      log.action,
      log.entity_type,
      log.entity_id,
      JSON.stringify(log.changes).replace(/,/g, ";")
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit_logs_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Audit logs exported successfully");
  };

  const handleExportTXT = () => {
    const lines = [
      "=" .repeat(80),
      "EMPULSEBIT HRMS - AUDIT LOG REPORT",
      `Generated: ${new Date().toLocaleString()}`,
      `Total Records: ${logs.length}`,
      "=".repeat(80),
      "",
    ];
    
    logs.forEach((log, index) => {
      lines.push(`[${index + 1}] ${log.created_at}`);
      lines.push(`    User: ${getUserName(log.user_id)}`);
      lines.push(`    Action: ${log.action}`);
      lines.push(`    Entity: ${log.entity_type} (${log.entity_id})`);
      lines.push(`    Changes: ${JSON.stringify(log.changes)}`);
      lines.push("-".repeat(60));
    });
    
    lines.push("");
    lines.push("=".repeat(80));
    lines.push("END OF REPORT");
    lines.push("=".repeat(80));
    
    const textContent = lines.join("\n");
    const blob = new Blob([textContent], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit_logs_${new Date().toISOString().split("T")[0]}.txt`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Audit logs exported as text file");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Audit Logs</h1>
          <p className="text-slate-500">Track all administrative actions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-audit-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={handleExportTXT} data-testid="export-audit-txt-btn">
            <FileText className="w-4 h-4 mr-2" />
            Export TXT
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={entityFilter} onValueChange={setEntityFilter}>
              <SelectTrigger className="w-48" data-testid="entity-filter-select">
                <SelectValue placeholder="All Entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {entityTypes.map((type) => (
                  <SelectItem key={type} value={type} className="capitalize">
                    {capitalizeFirst(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Changes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No audit logs found
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id} data-testid={`audit-log-${log.id}`}>
                    <TableCell className="whitespace-nowrap">
                      {formatDateTime(log.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium">
                          {getUserName(log.user_id).charAt(0)}
                        </div>
                        <span>{getUserName(log.user_id)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getActionColor(log.action)}>
                        {log.action}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <ClipboardList className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium capitalize">{capitalizeFirst(log.entity_type)}</p>
                          <p className="text-xs text-slate-400 font-mono">{log.entity_id.slice(0, 8)}...</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                        {JSON.stringify(log.changes).slice(0, 50)}...
                      </code>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogsPage;
